import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Calendar, 
  User, 
  Eye, 
  ArrowLeft, 
  Facebook, 
  Twitter, 
  Linkedin 
} from 'lucide-react';
import { NewsContent, newsService } from '../../../services/news';
import Navbar from '../navbar';

const NewsDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [news, setNews] = useState<NewsContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNewsDetail = async () => {
      try {
        if (id) {
          const fetchedNews = await newsService.getNewsById(Number(id));
          if (fetchedNews) {
            setNews(fetchedNews);
            setLoading(false);
          }
        }
      } catch (err) {
        setError('Gagal memuat detail berita');
        setLoading(false);
      }
    };

    fetchNewsDetail();
  }, [id]);

  const shareOnSocialMedia = (platform: 'facebook' | 'twitter' | 'linkedin') => {
    const url = window.location.href;
    const text = news?.title || 'Cek berita menarik ini!';

    const socialMediaUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };

    window.open(socialMediaUrls[platform], '_blank');
  };

  if (loading) return <div className="text-center py-10 text-lg font-semibold">Memuat...</div>;
  if (error) return <div className="text-red-500 text-center py-10 text-lg">{error}</div>;
  if (!news) return <div className="text-center py-10 text-lg font-semibold">Berita tidak ditemukan</div>;

  return (
    <div className="container mx-auto px-4 py-12">
      <Navbar />
      <div className="bg-white shadow-lg rounded-lg max-w-4xl mx-auto p-8">
        {/* Back Button */}
        <Link 
          to="/berita" 
          className="flex items-center text-indigo-600 hover:text-indigo-800 mb-6 text-lg font-medium"
        >
          <ArrowLeft className="mr-2" /> Kembali ke Daftar Berita
        </Link>

        {/* Article Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4">{news.title}</h1>

          {/* Metadata */}
          <div className="flex items-center text-gray-600 text-sm mb-6">
            <span className="flex items-center mr-4">
              <Calendar size={16} className="mr-2" /> 
              {new Date(news.created_at).toLocaleDateString('id-ID', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </span>
            <span className="flex items-center mr-4">
              <User size={16} className="mr-2" /> Admin
            </span>
            <span className="flex items-center">
              <Eye size={16} className="mr-2" /> 0 Dibaca
            </span>
          </div>

          {/* Social Share Buttons */}
          <div className="flex items-center space-x-3 mb-6">
            <button 
              onClick={() => shareOnSocialMedia('facebook')}
              className="text-blue-600 hover:text-blue-800"
            >
              <Facebook size={20} />
            </button>
            <button 
              onClick={() => shareOnSocialMedia('twitter')}
              className="text-blue-400 hover:text-blue-600"
            >
              <Twitter size={20} />
            </button>
            <button 
              onClick={() => shareOnSocialMedia('linkedin')}
              className="text-blue-700 hover:text-blue-900"
            >
              <Linkedin size={20} />
            </button>
          </div>
        </div>

        {/* Featured Image */}
        {news.image && (
          <img 
            src={`${process.env.REACT_APP_API_URL}${news.image}`}
            alt={news.title}
            className="w-full h-96 object-cover rounded-lg mb-8"
          />
        )}

        {/* Article Content */}
        <div className="prose lg:prose-xl text-gray-700">
          <div 
            className="prose"
            dangerouslySetInnerHTML={{ __html: news.description }}
          />
        </div>
      </div>
    </div>
  );
};

export default NewsDetail;
